import React, { useEffect, useState } from "react";
import clsx from 'clsx';
import { useTheme } from '@mui/material/styles';
import {
  AppBar,
  Toolbar,
  Typography,
  Hidden,
  IconButton,
  Button,
  ButtonGroup,
  Drawer,
  Paper,
  Divider,
  List,
  Popover,
  Tooltip,
  styled,
} from '@mui/material';
import { Menu, ChevronLeft, ChevronRight, FormatColorFill, BorderColor, FormatColorText } from '@mui/icons-material';
import logo from "../../assets/CURINOS_LOGO_W.png";
import "./Header.css";
import MenuItem from "./MenuItem";
import User from "./User";
import { Link } from "react-router-dom";
import { palettes } from "../../themes/palettes";
import { colorPaletteKey, displayDensityKey, displayDensity, userGroups, eventKeys, drawerWidth } from '../../common/constants';
import { Environment } from "../../environments/Environment";
import { EventEmitter } from "../../common/events";
import { getDefaultVerticalName } from "../../api/vertical.service";
import { useAuth } from "../../common/auth.context";

const PREFIX = 'Header';

const classes = {
  root: `${PREFIX}-root`,
  appBar: `${PREFIX}-appBar`,
  appBarShift: `${PREFIX}-appBarShift`,
  homeLink: `${PREFIX}-homeLink`,
  menuRoot: `${PREFIX}-menuRoot`,
  menuButton: `${PREFIX}-menuButton`,
  hide: `${PREFIX}-hide`,
  drawerHeader: `${PREFIX}-drawerHeader`,
  drawer: `${PREFIX}-drawer`,
  drawerPaper: `${PREFIX}-drawerPaper`,
  title: `${PREFIX}-title`,
  logo: `${PREFIX}-logo`,
  expandCollapseIcon: `${PREFIX}-expandCollapseIcon`,
  actionLinkSection: `${PREFIX}-actionLinkSection`,
  actionLink: `${PREFIX}-actionLink`,
  verticalNameLabel: `${PREFIX}-verticalNameLabel`,
  paletteSection: `${PREFIX}-paletteSection`,
  paletteBox: `${PREFIX}-paletteBox`,
  paletteSectionIcon: `${PREFIX}-paletteSectionIcon`,
  densitySection: `${PREFIX}-densitySection`,
  densitySectionIcon: `${PREFIX}-densitySectionIcon`,
  environmentText: `${PREFIX}-environmentText`,
  menuDivider: `${PREFIX}-menuDivider`
};

const Root = styled('span')((
  {
    theme
  }
) => ({
  [`&.${classes.root}`]: {
    flexGrow: 1,
  },

  [`& .${classes.appBar}`]: {
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    color: theme.palette.common.white
  },

  [`& .${classes.appBarShift}`]: {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: drawerWidth,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    })
  },

  [`& .${classes.homeLink}`]: {
    textDecoration: "none",
    display: "flex"
  },

  [`& .${classes.menuRoot}`]: {
    flexGrow: 1,
    backgroundColor: theme.palette.primary.main,
    paddingTop: '0',
  },

  [`& .${classes.menuButton}`]: {
    marginRight: theme.spacing(2)
  },

  [`& .${classes.hide}`]: {
    display: 'none',
  },

  [`& .${classes.drawerHeader}`]: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white
  },

  [`& .${classes.drawer}`]: {
    width: drawerWidth,
    flexShrink: 0,
  },

  [`& .${classes.drawerPaper}`]: {
    width: drawerWidth,
    borderRight: "none"
  },

  [`& .${classes.title}`]: {
    alignSelf: "center",
    color: theme.palette.common.white,
    flexGrow: 1,
    fontSize: "1.5rem"
  },

  [`& .${classes.logo}`]: {
    maxWidth: "100%",
    width: "auto",
    height: "auto",
    maxHeight: 35,
    marginTop: 5,
    marginRight: 10
  },

  [`& .${classes.expandCollapseIcon}`]: {
    color: theme.palette.common.white
  },

  [`& .${classes.actionLinkSection}`]: {
    flex: 1,
    textAlign: "right"
  },

  [`& .${classes.actionLink}`]: {
    color: theme.palette.common.white,
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(1)
  },

  [`& .${classes.verticalNameLabel}`]: {
    fontWeight: 500,
    padding: theme.spacing(1),
    cursor: "pointer",
    display: "none"
  },

  [`& .${classes.paletteSection}`]: {
    padding: theme.spacing(1)
  },

  [`& .${classes.environmentText}`]: {
    paddingLeft: 6,
    color: theme.palette.secondary.light,
    fontSize: "0.625rem"
  },

  [`& .${classes.menuDivider}`]: {
    backgroundColor: theme.palette.primary.light
  }
}));

const ThemeRoot = styled('span')((
  {
    theme
  }
) => ({

  [`& .${classes.paletteSection}`]: {
    padding: theme.spacing(1)
  },

  [`& .${classes.paletteBox}`]: {
    padding: theme.spacing(2),
    minWidth: 20
  },

  [`& .${classes.paletteSectionIcon}`]: {
    color: theme.palette.primary.main,
    marginRight: theme.spacing(1),
    verticalAlign: "bottom"
  },

  [`& .${classes.densitySection}`]: {
    padding: theme.spacing(1)
  },

  [`& .${classes.densitySectionIcon}`]: {
    color: theme.palette.primary.main,
    marginRight: theme.spacing(1),
    verticalAlign: "middle"
  }
}));

const Header = (props) => {

  const { roles } = useAuth();

  const theme = useTheme();

  const [verticalName, setVerticalName] = useState("");
  const [menuItems, setMenuItems] = useState([]);
  useEffect(() => {
    const allMenuItems = [
      { title: "Home", to: "/", icon: "home_rounded" },
      {
        title: "Configuration", to: "", icon: "build", subItems: [
          { title: "Institutions", to: "/lenders", icon: "account_balance" },
          { title: "Files", to: "/lenderfiles", icon: "file_copy_rounded" },
          { title: "Zones", to: "/config", icon: "list_rounded" },
          { title: "Adj. Manager", to: "/adjustmentmanager", icon: "horizontal_split", userGroups: [userGroups.Admin, userGroups.Mortgage] }
        ]
      },
      { title: "File Summary", to: "/filesummary", icon: "library_add_check" },
      { title: "Results", to: "/compare", icon: "compare_arrows_rounded" },
      { title: "Upload", to: "/manualprocess", icon: "cloud_upload_rounded" },
      { title: "Sync Rates", to: "/syncrates", icon: "sync" },
      { title: "Logs", to: "/log", icon: "error_outline_rounded" },
      {
        title: "Admin", to: "", icon: "supervisor_account", userGroups: [userGroups.Admin], subItems: [
          { title: "Settings", to: "/admin/settings", icon: "settings" },
          { title: "Users", to: "/admin/users", icon: "group" },
          { title: "Roles", to: "/admin/roles", icon: "work" },
          { title: "Import/Export", to: "/admin/importexport", icon: "import_export" }
        ]
      },
      { title: "Audit", to: "/admin/audit", icon: "security", userGroups: [userGroups.Auditor] }
    ];

    const checkSubMenuItems = (menuItem, currentUserGroups) => {
      if (menuItem.subItems) {
        let subMenuItems = [];
        menuItem.subItems.forEach((subMenuItem) => {
          if (subMenuItem.userGroups) {
            for (var i = 0; i < subMenuItem.userGroups.length; i++) {
              if (currentUserGroups.indexOf(subMenuItem.userGroups[i]) > -1) {
                if (subMenuItem.title === "Adj. Manager") {
                  if (Environment.enableAdjustmentManager === "true") {
                    subMenuItems.push(subMenuItem);
                  }
                }
                else {
                  subMenuItems.push(subMenuItem);
                }
                break;
              }
            }
          }
          else {
            subMenuItems.push(subMenuItem);
          }
        });
        menuItem.subItems = subMenuItems;
      }
    }

    const setUserMenu = async () => {
      if (props.isAuthenticated) {
        let menuItems = [];
        let currentUserGroups = roles;
        allMenuItems.forEach((menuItem) => {
          if (menuItem.userGroups) {
            for (var i = 0; i < menuItem.userGroups.length; i++) {
              if (currentUserGroups.indexOf(menuItem.userGroups[i]) > -1) {
                checkSubMenuItems(menuItem, currentUserGroups);
                menuItems.push(menuItem);
                break;
              }
            }
          }
          else {
            checkSubMenuItems(menuItem, currentUserGroups);
            menuItems.push(menuItem);
          }
        });
        setMenuItems(menuItems);
      }
      else {
        setMenuItems([]);
      }
    }

    setUserMenu();
  }, [props.isAuthenticated, roles]);

  const updateVerticalName = () => {
    getDefaultVerticalName()
      .then((verticalName) => {
        setVerticalName(verticalName);
      });
  }

  EventEmitter.subscribe(eventKeys.VerticalNameUpdate, updateVerticalName);

  const [popoverColorPaletteEl, setPopoverColorPaletteEl] = React.useState(null);

  const handleSelectColorPaletteClick = (event) => {
    setPopoverColorPaletteEl(event.currentTarget);
  };

  const handleSelectColorPaletteClose = () => {
    setPopoverColorPaletteEl(null);
  };

  const openColorPalettePopover = Boolean(popoverColorPaletteEl);
  const colorPalettePopoverId = openColorPalettePopover ? 'color-palette-popover' : undefined;

  const getColorPalettes = () => {
    let colorPalettes = [];
    for (var palette in palettes) {
      colorPalettes.push({ name: palette, title: palettes[palette].title, color: palettes[palette].primary });
    }

    return colorPalettes;
  }

  const handlePaletteColorClick = (e, name) => {
    localStorage.setItem(colorPaletteKey, name);
    window.location.reload(false);
  }

  const handleDisplayDensityClick = (e, name) => {
    localStorage.setItem(displayDensityKey, name);
    window.location.reload(false);
  }

  const handleDrawerOpen = () => {
    props.onMenuToggle(true);
  };

  const handleDrawerClose = () => {
    props.onMenuToggle(false);
  };

  return (
    <Root className={classes.root}>
      <AppBar position="fixed"
        className={clsx(classes.appBar, {
          [classes.appBarShift]: props.menuOpen,
        })}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            className={clsx(classes.menuButton, props.menuOpen && classes.hide)}
            title="Expand Menu"
            size="large">
            <Menu />
          </IconButton>
          <Link to="/" className={classes.homeLink}>
            <img src={logo} alt="Financial Intelligence" className={classes.logo} />
            <Typography variant="h2" className={classes.title}>
              <Hidden smDown={true}>Fulcrum<sup className={classes.environmentText}>{Environment.environment}</sup></Hidden>
            </Typography>
          </Link>
          <div className={classes.actionLinkSection}>
          
            <span className={classes.additionalText}>All times shown and used for processes are Eastern Time.</span>
 
            <Tooltip title="Vertical Name" arrow={true}><span className={classes.verticalNameLabel}>{verticalName}</span></Tooltip>
            <IconButton
              title="Customize Theme"
              aria-describedby={colorPalettePopoverId}
              variant="inherit"
              onClick={handleSelectColorPaletteClick}
              className={classes.actionLink}
              size="large">
              <FormatColorFill />
            </IconButton>
            <Popover
              id={colorPalettePopoverId}
              open={openColorPalettePopover}
              anchorEl={popoverColorPaletteEl}
              onClose={handleSelectColorPaletteClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <ThemeRoot>
                <div className={classes.paletteSection}>
                  <span title="Theme Color"><BorderColor className={classes.paletteSectionIcon} /></span>
                  <ButtonGroup variant="contained" color="primary" aria-label="contained primary button group">
                    {getColorPalettes().map(palette => {
                      return (<Button key={palette.name} variant="contained" className={classes.paletteBox} style={{ backgroundColor: palette.color }} title={palette.title} onClick={e => handlePaletteColorClick(e, palette.name)}></Button>)
                    })}
                  </ButtonGroup>
                </div>
                <div className={classes.densitySection}>
                  <span title="Font Size"><FormatColorText className={classes.densitySectionIcon} /></span>
                  <ButtonGroup variant="contained" color="primary" aria-label="contained primary button group">
                    {displayDensity.map(density => {
                      return (<Button key={density.name} size="small" variant="contained" color="primary" title={density.name} onClick={e => handleDisplayDensityClick(e, density.name)}>{density.name}</Button>)
                    })}
                  </ButtonGroup>
                </div>
              </ThemeRoot>
            </Popover>
            <User isAuthenticated={props.isAuthenticated} />
          </div>
        </Toolbar>
      </AppBar>
      <Drawer
        className={classes.drawer}
        variant="persistent"
        anchor="left"
        open={props.menuOpen}
        classes={{
          paper: classes.drawerPaper,
        }}
      >
        <Paper elevation={3} square className={classes.drawerHeader}>
          <IconButton onClick={handleDrawerClose} title="Collapse Menu" size="large">
            {theme.direction === 'ltr' ? <ChevronLeft className={classes.expandCollapseIcon} /> : <ChevronRight className={classes.expandCollapseIcon} />}
          </IconButton>
        </Paper>
        <Divider className={classes.menuDivider} />
        <List component="nav"
          className={classes.menuRoot}>
          {menuItems.map((menuItem, index) => {
            return (<React.Fragment key={index}>
              <MenuItem menuItem={menuItem} />
            </React.Fragment>);
          })}
        </List>
        <Divider className={classes.menuDivider} />
      </Drawer>
    </Root>
  );
};

export default Header;
